/**
 * MaterialOrderNudges — orange Command Center card listing materials
 * that need to be ordered to keep the schedule on track.
 *
 * Categories:
 *   - order_today      (order-by date has passed)
 *   - order_this_week  (1-7 days until order-by)
 *
 * Each row shows a "Mark ordered" button that records the order on the
 * phase and removes it from the feed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, AlertTriangle, CheckCircle2, ShoppingCart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const CATEGORY_CONFIG = {
  order_today: {
    icon: AlertTriangle,
    iconBg: '#dc2626',
    label: (n) =>
      n.days_until_order_by < 0
        ? `Overdue · ${Math.abs(n.days_until_order_by)}d past`
        : 'Order TODAY',
    pillBg: 'bg-red-100',
    pillText: 'text-red-800',
    pillBorder: 'border-red-200',
  },
  order_this_week: {
    icon: Package,
    iconBg: '#ea580c',
    label: (n) => `Order within ${n.days_until_order_by}d`,
    pillBg: 'bg-orange-100',
    pillText: 'text-orange-800',
    pillBorder: 'border-orange-200',
  },
};

export default function MaterialOrderNudges({ userId }) {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [orderingId, setOrderingId] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await api.get(`/users/${userId}/material-reminders`);
      setReminders(r.data?.reminders || []);
    } catch {
      setReminders([]);
    } finally {
      setLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const markOrdered = async (item) => {
    setOrderingId(item.phase_id);
    try {
      await api.post(`/phases/${item.phase_id}/mark-ordered`, {
        user_id: userId,
      });
      toast.success(`Marked "${item.item_name}" as ordered`);
      // Optimistically remove from list
      setReminders((prev) => prev.filter((r) => r.phase_id !== item.phase_id));
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not mark as ordered';
      toast.error(msg);
    } finally {
      setOrderingId(null);
    }
  };

  if (!loaded || reminders.length === 0) return null;

  return (
    <Card
      className="mb-4 overflow-hidden border-0 shadow-md"
      data-testid="material-order-nudges"
      style={{ background: 'linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%)' }}
    >
      <div className="px-4 py-3 flex items-center gap-2 border-b border-orange-300/50">
        <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center">
          <ShoppingCart className="w-3.5 h-3.5 text-white" />
        </div>
        <p className="text-xs font-bold uppercase tracking-wider text-orange-900">
          Procurement · {reminders.length} material{reminders.length === 1 ? '' : 's'} need{reminders.length === 1 ? 's' : ''} ordering
        </p>
      </div>
      <div className="divide-y divide-orange-300/40">
        {reminders.slice(0, 5).map((n) => {
          const cfg = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.order_this_week;
          const Icon = cfg.icon;
          const label = typeof cfg.label === 'function' ? cfg.label(n) : cfg.label;
          return (
            <div
              key={n.phase_id}
              className="px-4 py-3 flex items-start gap-3"
              data-testid={`material-nudge-${n.phase_id}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: cfg.iconBg }}
              >
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-sm font-bold text-orange-950 truncate">
                    {n.item_name}
                  </p>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${cfg.pillBg} ${cfg.pillText} ${cfg.pillBorder}`}
                  >
                    {label}
                  </span>
                  {n.is_custom && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                      Custom · {n.lead_time_days}d lead
                    </span>
                  )}
                </div>
                <p className="text-xs text-orange-900 leading-snug">
                  <strong>{n.project_name}</strong>
                  {n.vendor && <> · vendor: {n.vendor}</>}
                  {' · '}phase starts in {n.days_until_phase_starts}d
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs border-orange-400 text-orange-800 bg-white hover:bg-orange-50"
                  onClick={() => markOrdered(n)}
                  disabled={orderingId === n.phase_id}
                  data-testid={`mark-ordered-${n.phase_id}`}
                >
                  {orderingId === n.phase_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                  )}
                  Mark ordered
                </Button>
                <Button
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8"
                  onClick={() => navigate(`/project/${n.project_id}?tab=budget`)}
                  data-testid={`material-open-${n.phase_id}`}
                >
                  Open
                </Button>
              </div>
            </div>
          );
        })}
        {reminders.length > 5 && (
          <p className="px-4 py-2 text-[11px] text-orange-700 text-center">
            + {reminders.length - 5} more materials need ordering
          </p>
        )}
      </div>
    </Card>
  );
}
